import React, { useState, useRef } from 'react';
import { useAuth, canEditEquipment } from '../../context/AuthContext';
import { useCalibrationState } from '../../context/CalibrationPageContext';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FromControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TableRowsIcon from '@mui/icons-material/TableRows';
// import UploadFileIcon from '@mui/icons-material/UploadFileIcon';


function SensorGroup({ canEdit, sensorKey, sensors, onUpdate }) {
  const { setSensorKey, optionsData } = useCalibrationState();

  const [swapMode, setSwapMode] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState(new Set());
  const [initialRows, setInitialRows] = useState(null);
  const [localRows, setLocalRows] = useState(sensors);
  const [confirmDeleteopen, setConfirmDeleteOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);

  const [errorMsg, setErrorMsg] = useState('');

  const enterSwapMode = () => {
    setInitialRows([...sensors]);
    setLocalRows([...sensors]);
    setSelectedIndices(new Set());

    if (sensorKey !== 'LVDT Sensors'){
      toggleAll();
    }
    setSwapMode(true);
  };

  const exitSwapMode = () => {
    onUpdate(initialRows);
    setSwapMode(false);
    setSelectedIndices(new Set());
    setInitialRows(null);
  };

  const saveSwapMode = () => {
    onUpdate(localRows);
    setSwapMode(false);
    setSelectedIndices(new Set());
  };

  const swapSensors = () => {
    if (selectedIndices.size === selectedOptions.length) {
      const newRows = [...localRows];
      var idx = 0;
      for (const i of selectedIndices) {
        newRows[i] = selectedOptions[idx];
        idx ++;
      }

      setLocalRows(newRows);
      setSelectedIndices(new Set());
      setSelectedOptions([]);
    }
  };

  const toggleRow = (i) => {
    const next = new Set(selectedIndices);
    next.has(i) ? next.delete(i) : next.add(i);
    setSelectedIndices(next);
  };

  const toggleAll = () => {
    setSelectedIndices(
      selectedIndices.size === localRows.length
        ? new Set()
        : new Set(localRows.map((_, i) => i))
    );
  };

  const allChecked = selectedIndices.size === localRows.length && localRows.length > 0;
  const someChecked = selectedIndices.size > 0;

  {/* Read Mode */}
  if (!swapMode) {
    return (
      <Box>
        <Stack
          direction='row'
          display='flex'
          alignItems='center'
          mt={0.5}
        >
          <Divider
            orientation='horizontal'
            sx={{
              width: { xs: 16, sm: 32, md: 56 },
              mr: 1,
              borderBottom: 2
            }}
          />
          <Typography variant='body1' sx={{ fontSize: { xl: 16, md: 14, sm: 12 } }}>
            {sensorKey}
          </Typography>
          <Divider
            orientation='horizontal'
            sx={{
              flexGrow: 1,
              ml: 1, mr: 1,
              borderBottom: 2
            }}
          />

          <Button
            size='small'
            variant='outlined'
            startIcon={<SwapHorizIcon sx={{ fontSize: { xl: 18, md: 16, sm: 14 } }} />}
            onClick={enterSwapMode}
            sx={{
              fontSize: { xl: 16, md: 14, sm: 12 },
              backgroundColor: 'background.paper',
              ':hover': { backgroundColor: 'background.paper' },
            }}
          >
            Swap Sensors
          </Button>
        </Stack>

        {sensors.map((calInfo, calIndex) => (
          <Grid
            container
            alignItems='center'
            key={calInfo.SerialNumber + calIndex}
            sx={{
              p: 1, mt: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper'
            }}
          >
            <Grid item xs={4} justifyItems='center'>
              <Typography variant='body1' sx={{ fontSize: { xl: 16, md: 14, sm: 12 } }}>
                {calInfo.SerialNumber}
              </Typography>
            </Grid>

            <Grid item xs={4} justifyItems='center'>
              <Typography variant='body1' sx={{ fontSize: { xl: 16, md: 14, sm: 12} }}>
                {calInfo.CalibrationFactor}
              </Typography>
            </Grid>
            <Grid item xs={4} justifyItems='center'>
              <Typography variant='body1' sx={{ fontSize: { xl: 16, md: 14, sm: 12 } }}>
                {calInfo.TestDate}
              </Typography>
            </Grid>
          </Grid>
        ))}
      </Box>
    );
  }

  {/* Swap Mode */}
  return (
    <Box>
      <Stack
        direction='row'
        display='flex'
        alignItems='center'
        mb={1}
      >
        <Divider
          orientation='horizontal'
          sx={{
            width: { xs: '16px', sm: '56px' },
            mr: 1,
            borderBottom: 2
          }}
        />
        <Typography variant='body1'>
          {sensorKey}
        </Typography>
        <Chip
          size='small'
          label='Editing'
          color='primary'
          sx={{ ml: 1, color: 'background.paper' }}
        />
        <Divider
          orientation='horizontal'
          sx={{
            flexGrow: 1,
            ml: 1, mr: 1,
            borderBottom: 2
          }}
        />
        <Stack
          direction='row'
          spacing={1}
          sx={{ flexShrink: 0 }}
        >
          <Button
            size='small'
            variant='outlined'
            onClick={() => exitSwapMode()}
            sx={{
              backgroundColor: 'background.paper',
              ':hover': { backgroundColor: 'background.paper' }
            }}
          >
            Cancel
          </Button>
          <Button
            size='small'
            variant='contained'
            onClick={saveSwapMode}
          >
            Done
          </Button>
        </Stack>
      </Stack>

      <Grid
        container
        alignItems='center'
        sx={{ px: 1, mb: 0.5 }}
      >
        <Grid item xs={2} sm={1} display='flex' justifyContent='center'>
          <Checkbox
            size='small'
            checked={allChecked}
            indeterminate={someChecked && !allChecked}
            onChange={toggleAll}
          />
        </Grid>
        {/* Phone: the picker + buttons wrap onto their own full-width line */}
        <Grid item xs={12} sm={11} display='flex' justifyContent='right'>
          <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' rowGap={1} justifyContent='flex-end' sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Autocomplete
              size='small'
              multiple
              id={`swap-${sensorKey}-sensors`}
              options={optionsData[sensorKey] || []}
              value={selectedOptions}
              onChange={(event, newValue) => { setSelectedOptions(newValue); }}
              getOptionLabel={(option) => option.SerialNumber}
              renderOption={(props, option) => (
                <li {...props}>
                  <div>
                    <strong>{option.SerialNumber}</strong>
                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                      CalibrationFactor: {option.CalibrationFactor}
                    </div>

                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                      Test Date: {option.TestDate}
                    </div>
                  </div>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label='Select Load Cells' />
              )}

              sx={{
                width: { xs: '100%', sm: '300px' },
                borderRadius: 1,
                backgroundColor: 'background.paper',
                ':hover': { backgroundColor: 'background.paper' }
              }}
            />

            <Button
              variant='contained'
              size='small'
              disabled={!(someChecked && selectedOptions.length > 0)}
              onClick={swapSensors}
            >
              Swap
            </Button>

          </Stack>

        </Grid>
      </Grid>

      {localRows.map((row, i) => (
        <Grid
          container
          alignItems='center'
          key={i}
          sx={{
            p: 1, mb: 1, borderRadius: 1,
            border: '1px solid',
            borderColor: selectedIndices.has(i) ? 'primary.main' : 'divider',
            backgroundColor: 'background.paper'
          }}
        >
          <Grid item xs={2} sm={1} display='flex' justifyContent='center'>
            <Checkbox
              size='small'
              checked={selectedIndices.has(i)}
              onChange={() => toggleRow(i)}
              sx={{
                p: 0.25
              }}
            />
          </Grid>

          <Grid item sm={4} justifyItems='center'>
            <Typography variant='body1'>
              {row.SerialNumber}
            </Typography>
          </Grid>

          <Grid item sm={3} justifyItems='center'>
            <Typography variant='body1'>
              {row.CalibrationFactor}
            </Typography>
          </Grid>

          <Grid item sm={4} justifyItems='center'>
            <Typography variant='body1'>
              {row.TestDate}
            </Typography>
          </Grid>
        </Grid>
      ))}
    </Box>
  );

}

export default SensorGroup;